/**
 * Tenant Admin estimate queue — lists every in-progress estimate
 * under the caller's tenant. Read-only in Phase 2; the submit /
 * review / approve actions land in Phase 5.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { listAdminEstimates } from "@starz/shared/api/sdk.gen";
import type { AdminEstimate, EstimateStatus } from "@starz/shared/api/types.gen";
import { ROOM_TYPE_LABEL } from "@starz/shared/api/types.gen";

const FILTERS: { label: string; status: EstimateStatus }[] = [
  { label: "Submitted", status: "submitted" },
  { label: "In review", status: "in_review" },
  { label: "Action needed", status: "info_requested" },
  { label: "Approved", status: "approved" },
  { label: "In progress", status: "in_progress" },
  { label: "Rejected", status: "rejected" },
];

function projectLabel(row: AdminEstimate): string {
  if (row.room_label) return row.room_label;
  if (row.room_type) return ROOM_TYPE_LABEL[row.room_type];
  return `Estimate ${row.estimate_id.slice(-6).toUpperCase()}`;
}

export function AdminQueue() {
  const [items, setItems] = useState<AdminEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<EstimateStatus>("submitted");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await listAdminEstimates({ status, limit: 50 });
      if (cancelled) return;
      if ("error" in result) {
        setError(result.error.error.message);
      } else {
        setItems(result.data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <div className="mx-auto max-w-content px-6 py-12 md:px-12 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Review queue
      </p>
      <h1 className="mb-3 font-serif text-4xl text-starz-navy">
        In-progress <em className="text-starz-gold">estimates</em>.
      </h1>
      <div className="mb-4 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />
      <p className="mb-4 text-sm text-warm-gray">
        Manage your{" "}
        <Link to="/admin/catalog" className="text-starz-gold underline">
          price book
        </Link>{" "}
        in the catalog, or your{" "}
        <Link to="/admin/users" className="text-starz-gold underline">
          team &amp; users
        </Link>
        .
      </p>
      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            type="button"
            key={f.status}
            onClick={() => setStatus(f.status)}
            className={
              "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest transition-colors " +
              (status === f.status
                ? "bg-starz-navy text-starz-off-white"
                : "bg-starz-cream/60 text-starz-charcoal hover:bg-starz-cream")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-warm-gray">Loading…</p>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-starz-gold/15 bg-starz-cream p-10 text-center text-warm-gray">
          No estimates yet. New customer conversations will appear here as they happen.
        </div>
      ) : (
        <table className="min-w-full divide-y divide-starz-gold/15 rounded-xl border border-starz-gold/15 bg-white text-sm">
          <thead className="bg-starz-cream/60 text-left text-xs uppercase tracking-widest text-starz-charcoal">
            <tr>
              <th className="px-5 py-3">Project</th>
              <th className="px-5 py-3">Customer</th>
              <th className="px-5 py-3">Last activity</th>
              <th className="px-5 py-3">Tokens</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-starz-gold/10">
            {items.map((row) => {
              const fullName = [row.customer_first_name, row.customer_last_name]
                .filter(Boolean)
                .join(" ");
              return (
                <tr key={row.estimate_id}>
                  <td className="px-5 py-4">
                    <div className="font-medium text-starz-navy">{projectLabel(row)}</div>
                    {row.room_label && row.room_type && (
                      <div className="text-xs text-warm-gray">{ROOM_TYPE_LABEL[row.room_type]}</div>
                    )}
                    <div className="mt-1 font-mono text-xs text-warm-gray/70">
                      ID {row.estimate_id.slice(-8).toUpperCase()}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="font-medium text-starz-navy">
                      {fullName || row.customer_email || row.customer_user_id}
                    </div>
                    {row.customer_email && (
                      <div className="text-xs text-warm-gray">{row.customer_email}</div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-warm-gray">
                    {new Date(row.last_message_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-4 text-xs text-warm-gray">
                    {row.total_input_tokens} in / {row.total_output_tokens} out
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link
                      to={`/admin/estimates/${row.estimate_id}`}
                      className="text-sm font-semibold text-starz-gold hover:underline"
                    >
                      Review →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
